import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Shield,
  LocalShipping,
  VerifiedUser,
  ExpandMore,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Product } from "../../types";

interface ProductAdditionalInfoProps {
  product: Product;
}

const ProductAdditionalInfo: React.FC<ProductAdditionalInfoProps> = ({ product }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 6, px: 3, pb: 3 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 4,
          pb: 2,
          borderBottom: "2px solid var(--border-primary)",
          color: "text.primary",
        }}
      >
        {t("products.additionalInformation")}
      </Typography>

      {/* Info Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        <Box>
          <Card
            sx={{
              height: "100%",
              border: "1px solid var(--border-primary)",
              bgcolor: "var(--bg-secondary)",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Shield sx={{ color: "var(--primary-color)", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("warrantySupport.title")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {product.warranty_duration
                  ? t("warrantySupport.comprehensiveWarranty", {
                      months: product.warranty_duration,
                    })
                  : t("warrantySupport.standardWarranty")}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card
            sx={{
              height: "100%",
              border: "1px solid var(--border-primary)",
              bgcolor: "var(--bg-secondary)",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <LocalShipping sx={{ color: "var(--success-color)", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("products.delivery")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("products.freeDeliveryEurope")}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card
            sx={{
              height: "100%",
              border: "1px solid var(--border-primary)",
              bgcolor: "var(--bg-secondary)",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <VerifiedUser sx={{ color: "var(--warning-color)", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("qualityAssurance.title")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("qualityAssurance.rigorousTesting")}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Detailed Accordions */}
      <Box sx={{ my: 4 }}>
        <Box>
          <Accordion
            defaultExpanded
            sx={{
              border: "none",
              boxShadow: "none",
              "&:hover": { border: "none", boxShadow: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "var(--bg-hover)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("products.offerDetails")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {t("products.productDetail.offerDetailsBody")}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 3,
            }}
          >
            <Stack spacing={2}>
              <Accordion
                defaultExpanded
                sx={{
                  mt: 2,
                  border: "none",
                  boxShadow: "none",
                  "&:hover": { border: "none", boxShadow: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "var(--bg-hover)" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("products.description")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                    {product.description?.trim()
                      ? product.description
                      : t("products.noDescription")}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  border: "none",
                  boxShadow: "none",
                  "&:hover": { border: "none", boxShadow: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "var(--bg-hover)" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("products.keyFeatures")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div" variant="body2" color="text.secondary">
                    <ul style={{ paddingLeft: "1.2rem", margin: 0 }}>
                      <li>{t("products.productDetail.features.item1")}</li>
                      <li>{t("products.productDetail.features.item2")}</li>
                      <li>{t("products.productDetail.features.item3")}</li>
                      <li>{t("products.productDetail.features.item4")}</li>
                      <li>{t("products.productDetail.features.item5")}</li>
                      <li>{t("products.productDetail.features.item6")}</li>
                      <li>{t("products.productDetail.features.item7")}</li>
                    </ul>
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Stack>

            <Stack spacing={2}>
              <Accordion
                sx={{
                  border: "none",
                  boxShadow: "none",
                  "&:hover": { border: "none", boxShadow: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "var(--bg-hover)" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("products.specifications")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div" variant="body2" color="text.secondary">
                    <ul style={{ paddingLeft: "1.2rem", margin: 0 }}>
                      <li>{t("products.productDetail.technical.item1")}</li>
                      <li>{t("products.productDetail.technical.item2")}</li>
                      <li>{t("products.productDetail.technical.item3")}</li>
                      <li>{t("products.productDetail.technical.item4")}</li>
                      <li>{t("products.productDetail.technical.item5")}</li>
                      <li>{t("products.productDetail.technical.item6")}</li>
                      <li>{t("products.productDetail.technical.item7")}</li>
                      <li>{t("products.productDetail.technical.item8")}</li>
                      <li>{t("products.productDetail.technical.item9")}</li>
                    </ul>
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  border: "none",
                  boxShadow: "none",
                  "&:hover": { border: "none", boxShadow: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "var(--bg-hover)" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("products.compatibleAccessories")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {t("products.productDetail.compatibleAccessoriesBody")}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductAdditionalInfo;